import type { CrowdParticipant, CrowdSession } from "../models/contracts";

export interface TextWallPhone {
  index: number;
  character: string;
  participant: CrowdParticipant | null;
}

const characterAt = (text: string | null, index: number): string => {
  const value = (text ?? "").trim();
  if (!value.length) return " ";
  const Segmenter = (Intl as typeof Intl & { Segmenter?: new (locale?: string | string[], options?: { granularity: "grapheme" }) => { segment(value: string): Iterable<{ segment: string }> } }).Segmenter;
  const elements = Segmenter
    ? Array.from(new Segmenter(undefined, { granularity: "grapheme" }).segment(value), item => item.segment)
    : Array.from(value);
  return elements[index % elements.length] ?? " ";
};

export function buildTextWallPhones(crowd: CrowdSession): TextWallPhone[] {
  const participants = crowd.participants ?? [];
  const tiles = crowd.tiles ?? [];
  const highestAssignment = participants.reduce((maximum, participant) =>
    Math.max(maximum, participant.assignedIndex ?? -1), -1);
  const highestTile = tiles.reduce((maximum, tile) => Math.max(maximum, tile.index), -1);
  const count = Math.max(1, crowd.expectedParticipantCount, highestAssignment + 1, highestTile + 1);

  return Array.from({ length: count }, (_, index) => {
    const participant = participants.find(value => value.assignedIndex === index) ?? null;
    const tile = tiles.find(value => value.index === index);
    return {
      index,
      participant,
      character: participant?.assignedCharacter ?? tile?.character ?? characterAt(crowd.textContent, index),
    };
  });
}

export function textWallGrid(count: number): { columns: number; rows: number } {
  const safeCount = Math.max(1, count);
  const rows = safeCount <= 10 ? 1 : 2;
  return { columns: Math.ceil(safeCount / rows), rows };
}

export function textWallPhoneSize(
  viewportWidth: number,
  viewportHeight: number,
  columns: number,
  rows: number,
): { width: number; height: number; gap: number } {
  const safeColumns = Math.max(1, columns);
  const safeRows = Math.max(1, rows);
  const gap = Math.max(8, Math.min(18, viewportWidth * 0.0085));
  const horizontalPadding = Math.max(48, Math.min(190, viewportWidth * 0.05)) * 2;
  const verticalPadding = Math.max(30, Math.min(70, viewportHeight * 0.04))
    + Math.max(28, Math.min(68, viewportHeight * 0.04));
  const headingHeight = Math.max(105, Math.min(165, viewportHeight * 0.16));
  const headingGap = Math.max(18, Math.min(40, viewportHeight * 0.024));
  const availableWidth = Math.max(1, viewportWidth - horizontalPadding - gap * (safeColumns - 1));
  const availableHeight = Math.max(1, viewportHeight - verticalPadding - headingHeight - headingGap - gap * (safeRows - 1));
  const widthFromColumns = availableWidth / safeColumns;
  const heightFromRows = availableHeight / safeRows;
  const phoneWidthToHeight = 9 / 19.5;
  const height = Math.max(1, Math.min(heightFromRows, widthFromColumns / phoneWidthToHeight));

  return { width: height * phoneWidthToHeight, height, gap };
}
